import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, Copy, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, SectionLabel } from "@/components/bits";
import PostPurchase from "@/components/PostPurchase";
import PurchaseUpsellModal from "@/components/PurchaseUpsellModal";
import DecodingKey from "@/components/DecodingKey";
import FlexCard from "@/components/FlexCard";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user } = useAuth();
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout
  const [order, setOrder] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;

    const fetchOrder = async () => {
      try {
        const { data } = await api.get(`/orders/by-session/${sessionId}`);
        if (data && (data.license_key || data.key_status)) setOrder(data);
      } catch { /* not fulfilled yet */ }
      // Also link the order to the user if they happen to be logged in
      if (user && user.role) {
        try { await api.post(`/orders/claim/${sessionId}`); } catch { /* ignore */ }
      }
    };

    const poll = async () => {
      if (!active) return;
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          await fetchOrder();
          return;
        }
        if (["failed", "expired"].includes(data.status)) { setState("failed"); return; }
      } catch { /* ignore transient */ }
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, user]);

  const copy = (k) => { navigator.clipboard.writeText(k); toast.success("Copied"); };

  return (
    <div className="min-h-screen flex items-center justify-center px-5" data-testid="payment-success-page">
      <div className="w-full max-w-lg text-center">
        <Logo size={48} withText={false} />
        {state === "polling" && (
          <div className="mt-8">
            <Loader2 size={40} className="mx-auto animate-spin text-white/70" />
            <SectionLabel className="justify-center mt-8">VERIFYING PAYMENT</SectionLabel>
            <p className="mt-3 text-white/50">Confirming with Square. Do not close this window...</p>
          </div>
        )}

        {state === "paid" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <CheckCircle2 size={44} className="mx-auto text-white mt-8" />
            <h1 className="mt-6 font-display text-4xl sm:text-5xl uppercase">Payment Confirmed</h1>
            <p className="mt-4 text-white/55">Your order is complete. Welcome to the winning side.</p>

            {order && (order.items || []).length > 1 ? (
              <div className="mt-8 rounded-2xl border border-white/25 p-6 text-left" data-testid="license-panel">
                <div className="font-mono text-[10px] tracking-[0.2em] text-white/40">{order.items.length} ITEMS IN THIS ORDER</div>
                {order.items.map((it, ii) => (
                  <div key={ii} className="mt-5 border-t border-white/10 pt-4 first:border-t-0 first:pt-0" data-testid={`order-item-${ii}`}>
                    <div className="font-mono text-[10px] tracking-[0.2em] text-white/60">
                      {it.product_name} <span className="text-white/35">// {it.tier}{(it.quantity || 1) > 1 ? ` ×${it.quantity}` : ""}</span>
                    </div>
                    {(it.keys || []).length > 0 ? (
                      (it.keys).map((k, i) => (
                        <div key={i} className="mt-3 flex items-center gap-3">
                          <DecodingKey value={k} tid={`license-key-${ii}-${i}`} className="font-mono text-white bg-white/[0.08] px-3 py-2 rounded-lg border border-white/15 flex-1 break-all" />
                          <button onClick={() => copy(k)} data-testid={`copy-license-key-${ii}-${i}`} className="text-white/60 hover:text-white" title="Copy key"><Copy size={18} /></button>
                        </div>
                      ))
                    ) : (
                      <p className="mt-3 font-mono text-[10px] tracking-[0.15em] text-white/50">// KEY ON THE WAY — EMAILED SHORTLY</p>
                    )}
                  </div>
                ))}
              </div>
            ) : order && (order.keys?.length || order.license_key) ? (
              <div className="mt-8 rounded-2xl border border-white/25 p-6 text-left" data-testid="license-panel">
                <div className="font-mono text-[10px] tracking-[0.2em] text-white/40">
                  {order.product_name}{order.quantity > 1 ? ` — ${(order.keys || []).length} OF ${order.quantity} KEYS` : ""}
                </div>
                {(order.keys?.length ? order.keys : [order.license_key]).filter(Boolean).map((k, i) => (
                  <div key={i} className="mt-3 flex items-center gap-3">
                    <DecodingKey value={k} tid={`license-key-${i}`} className="font-mono text-white bg-white/[0.08] px-3 py-2 rounded-lg border border-white/15 flex-1 break-all" />
                    <button onClick={() => copy(k)} data-testid={`copy-license-key-${i}`} className="text-white/60 hover:text-white" title="Copy key"><Copy size={18} /></button>
                  </div>
                ))}
                {order.key_status === "partial" && (
                  <p className="mt-4 font-mono text-[10px] tracking-[0.15em] text-white/60" data-testid="partial-note">
                    // REMAINING KEYS ARE RESTOCKING — THEY'LL BE EMAILED TO YOU SHORTLY
                  </p>
                )}
                <p className="mt-4 font-mono text-[10px] tracking-[0.15em] text-white/50">
                  // SAVE {order.quantity > 1 ? "THESE KEYS" : "THIS KEY"} NOW — SCREENSHOT OR COPY. {order.buyer_email ? `ALSO EMAILED TO ${order.buyer_email}. ` : ""}FOR PERMANENT ACCESS,{" "}
                  {user && user.role ? "THEY'RE ALSO IN YOUR DASHBOARD." : (
                    <Link to="/register" className="underline text-white">CREATE A FREE ACCOUNT</Link>
                  )}
                </p>
              </div>
            ) : order ? (
              <div className="mt-8 rounded-2xl border border-white/25 p-6 text-left" data-testid="license-pending-panel">
                <div className="flex items-center gap-3">
                  <Clock size={18} className="text-white/70 shrink-0" />
                  <div className="font-mono text-[10px] tracking-[0.2em] text-white/40">{order.product_name} — KEY BEING PREPARED</div>
                </div>
                <p className="mt-4 text-sm text-white/60 leading-relaxed">
                  Your key is being prepared and will be emailed to{" "}
                  <span className="text-white font-mono">{order.buyer_email || "your inbox"}</span> shortly — usually within a few hours.
                </p>
              </div>
            ) : (
              <p className="mt-6 font-mono text-xs tracking-[0.15em] text-white/50">
                // FINALISING YOUR KEY... IF IT DOESN'T APPEAR IN A FEW SECONDS, PLEASE CONTACT SUPPORT ON DISCORD.
              </p>
            )}

            {order && (
              <div className="mt-6 flex justify-center">
                <FlexCard order={order} buttonTestId="flex-card-open" />
              </div>
            )}
            {order && <PostPurchase sessionId={sessionId} order={order} productSlug={order.slug} />}
            {order && <PurchaseUpsellModal sessionId={sessionId} />}

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              {user && user.role
                ? <Link to="/dashboard" data-testid="success-dashboard-btn" className="btn-inj px-8 py-4 text-sm">GO TO DASHBOARD</Link>
                : <Link to="/register" data-testid="success-register-btn" className="btn-ghost px-8 py-4 text-sm">SAVE TO ACCOUNT (OPTIONAL)</Link>}
              <Link to="/store" className="btn-ghost px-8 py-4 text-sm">KEEP SHOPPING</Link>
            </div>
          </motion.div>
        )}

        {(state === "failed" || state === "timeout") && (
          <div className="mt-8">
            <XCircle size={44} className="mx-auto text-white/60" />
            <h1 className="mt-6 font-display text-4xl uppercase">{state === "timeout" ? "Still Processing" : "Payment Issue"}</h1>
            <p className="mt-4 text-white/55">
              {state === "timeout"
                ? "This is taking longer than expected. Check your email shortly or contact support."
                : "We couldn't confirm your payment. If you were charged, contact support on Discord."}
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              <Link to="/store" className="btn-inj px-8 py-4 text-sm">BACK TO STORE</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
